import { LocationMarkerIcon, TrashIcon } from "@heroicons/react/solid";
import dayjs from "dayjs";
import { SetStateAction, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NavBar from "../components/navbar";
import NewComment from "../components/newcomment";
import { threadContentApi, getCommentContentApi } from "../services/thread";
import { Scrollbars } from "react-custom-scrollbars-2";
import Pagination from "../components/shared/Pagination.js";
import "../components/shared/Pagination.css";
import { useAuth } from "../context/authprovider";
import Confirmation from "../components/confirmation";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import {
  AdvancedImage,
  lazyload,
  accessibility,
  responsive,
  placeholder,
} from "@cloudinary/react";
import { cld } from "..";
import { fill } from "@cloudinary/url-gen/actions/resize";
import { focusOn } from "@cloudinary/url-gen/qualifiers/gravity";
import { FocusOn } from "@cloudinary/url-gen/qualifiers/focusOn";
import { byRadius } from "@cloudinary/url-gen/actions/roundCorners";

export type ThreadContent = {
  id: number;
  topicId: number;
  createdBy: string;
  threadsTitle: string;
  threadsContent: string;
  createdDate: number;
  lastUpdated: number | null;
  userId: string;
  userProfilePic: string;
};

export type CommentContent = {
  id: number;
  commentContent: string;
  createdDate: number;
  commentBy: string;
  userId: string;
  userProfilePic: string;
};

const Thread = (props: any) => {
  const { topicId, threadId } = useParams();
  const [commentContent, setCommentContent] = useState<CommentContent[]>();
  const [threadContent, setThreadContent] = useState<ThreadContent>();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(4);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCommentIndex, setSelectedCommentIndex] = useState(-1);
  const [selectedThreadIndex, setSelectedThreadIndex] = useState(-1);
  const [deleteType, setDeleteType] = useState("");
  const { userId, role } = useAuth();
  let navigate = useNavigate();

  useEffect(() => {
    const loadThreadContent = async () => {
      try {
        const threadContent = await threadContentApi(topicId, threadId);
        const comments = await getCommentContentApi(threadId);

        if (threadContent) {
          setThreadContent(threadContent.threadContent);
        }
        if (comments) {
          setCommentContent(comments.commentContent);
        }
      } catch (e) {
        if (e instanceof AxiosError) {
          navigate("/NotFound");
        }
      }
    };
    loadThreadContent();
  }, []);

  const image = cld
    .image("pxy3dqvsnmwvkofamgkx")
    .resize(fill().width(100).height(100).gravity(focusOn(FocusOn.faces())))
    .roundCorners(byRadius(20));

  //get current page
  const indexofLastdata = currentPage * rowsPerPage;
  const indexofFirstdata = indexofLastdata - rowsPerPage;
  var currentdata = commentContent?.slice(indexofFirstdata, indexofLastdata);

  // Change page
  const paginate = (pageNumber: SetStateAction<number>) =>
    setCurrentPage(pageNumber);

  return (
    <>
      <NavBar />
      <div className="mt-20">
        <br></br>
        <div className="flex flex-wrap mx-72 mb-6">
          <div
            className="place-item-start"
            style={{
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <p>Title: {threadContent?.threadsTitle}</p>
          </div>
        </div>
        <Scrollbars
          autoHeight
          autoHeightMin={100}
          autoHeightMax="calc(60vh - 36px - 35px - 75px)"
        >
          <div className="flex flex-wrap">
            <div className="xl:mx-72 h-fit flex-auto bg-white object-center shadow overflow-hidden sm:rounded-md w-5/6">
              <ul role="list" className="divide-y divide-gray-200">
                <li key={threadContent?.id}>
                  {openModal && (
                    <Confirmation
                      show={openModal}
                      userId={userId!}
                      id={threadContent?.id!}
                      // threadId={threadContent?.id!}
                      // commentId={-1}
                      onToggle={setOpenModal}
                      selectedIndex={setSelectedCommentIndex}
                      deleteType={deleteType}
                    />
                  )}
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-indigo-600 truncate">
                        {threadContent?.userProfilePic == null ? (
                          <AdvancedImage
                            cldImg={cld
                              .image("NewUser.webp")
                              .resize(
                                fill()
                                  .width(100)
                                  .height(100)
                                  .gravity(focusOn(FocusOn.faces()))
                              )
                              .roundCorners(byRadius(20))}
                          />
                        ) : (
                          <AdvancedImage
                            cldImg={cld
                              .image(threadContent?.userProfilePic)
                              .resize(
                                fill()
                                  .width(100)
                                  .height(100)
                                  .gravity(focusOn(FocusOn.faces()))
                              )
                              .roundCorners(byRadius(20))}
                          />
                        )}
                      </p>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between">
                      <div className="sm:flex">
                        <p className="flex items-center text-sm text-gray-500">
                          Started by: {threadContent?.createdBy}
                        </p>
                        <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                          <LocationMarkerIcon
                            className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                            aria-hidden="true"
                          />
                          {dayjs
                            .unix(threadContent?.createdDate!)
                            .format("DD MMM YYYY,h:mm A")}
                        </p>

                        {role === "admin" ? (
                          <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                            <button
                              type="button"
                              onClick={() => {
                                setDeleteType("deleteThread");
                                setOpenModal(true);
                                setSelectedThreadIndex(threadContent?.id!);
                              }}
                            >
                              <TrashIcon
                                className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                                aria-hidden="true"
                              />
                            </button>
                          </p>
                        ) : (
                          <div></div>
                        )}
                      </div>

                      <div className="sm:flex">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: threadContent?.threadsContent!,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </li>

                {/* Populating comments */}
                {currentdata &&
                  currentdata.map((comment) => (
                    <li key={comment.id}>
                      {openModal && comment.id == selectedCommentIndex && (
                        <Confirmation
                          show={openModal}
                          userId={userId!}
                          id={comment.id}
                          onToggle={setOpenModal}
                          selectedIndex={setSelectedCommentIndex}
                          deleteType={deleteType}
                        />
                      )}
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-indigo-600 truncate">
                            {threadContent?.userProfilePic == null ? (
                              <AdvancedImage
                                cldImg={cld
                                  .image("rwrilx1xfsynpw7zuboa")
                                  .resize(
                                    fill()
                                      .width(100)
                                      .height(100)
                                      .gravity(focusOn(FocusOn.faces()))
                                  )
                                  .roundCorners(byRadius(20))}
                              />
                            ) : (
                              <AdvancedImage
                                cldImg={cld
                                  .image(threadContent?.userProfilePic)
                                  .resize(
                                    fill()
                                      .width(100)
                                      .height(100)
                                      .gravity(focusOn(FocusOn.faces()))
                                  )
                                  .roundCorners(byRadius(20))}
                              />
                            )}
                          </p>
                        </div>
                        <div className="mt-2 sm:flex sm:justify-between">
                          <div className="sm:flex">
                            <p className="flex items-center text-sm text-gray-500">
                              Started by: {comment.commentBy}
                            </p>
                            <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                              <LocationMarkerIcon
                                className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                                aria-hidden="true"
                              />
                              {dayjs
                                .unix(comment.createdDate)
                                .format("DD MMM YYYY,h:mm A")}
                            </p>

                            {comment?.userId === userId ? (
                              <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setDeleteType("deleteComment");
                                    setOpenModal(true);
                                    setSelectedCommentIndex(comment.id);
                                  }}
                                >
                                  <TrashIcon
                                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                                    aria-hidden="true"
                                  />
                                </button>
                              </p>
                            ) : (
                              <div></div>
                            )}
                          </div>
                          <div className="sm:flex">
                            <div
                              dangerouslySetInnerHTML={{
                                __html: comment?.commentContent!,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </Scrollbars>
        <div
          className="place-item-start"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Pagination
            postsPerPage={rowsPerPage}
            totalPosts={commentContent?.length}
            paginate={paginate}
          />
        </div>
        <div className="flex flex-row w-full">
          <div className="basis-full xl:mx-72 mt-6">
            <NewComment />
          </div>
        </div>
      </div>
    </>
  );
};

export default Thread;
